import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCheckCircle,
  FiChevronLeft,
  FiClock,
  FiInfo,
  FiMapPin,
  FiMinusCircle,
  FiPlusCircle,
  FiRefreshCw,
  FiXCircle
} from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import ZonePickerModal from '@/lib/shared-coms/ZonePickerModal';
import type { ChosenZone } from '@/lib/shared-coms/ZonePickerModal/ZonePickerModal';
import useZoneScanLookup from '@/lib/shared-coms/ZoneScanner/useZoneScanLookup';
import type { ScannedZone } from '@/lib/shared-coms/ZoneScanner/ZoneScanOverlay';
import { SCAN_PROFILE, useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { playInfoTone, playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { looksLikeProductScan } from '@/lib/scanning/retailCode';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import ZoneCountProductCard from './com/ZoneCountProductCard';
import ZoneCountFoundList from './com/ZoneCountFoundList';
import ZoneCountFoundStockSheet, { type FoundStockDraft } from './com/ZoneCountFoundStockSheet';
import ZoneCountManualEntry from './com/ZoneCountManualEntry';
import ZoneCountReview from './com/ZoneCountReview';
import type { ZoneCountApplyResult, ZoneCountApplyVariables } from './ZoneCount.data';
import {
  addFoundUnit,
  applyInput,
  bumpBulkCount,
  clearDraft,
  countSummary,
  countUnits,
  defaultSelection,
  draftCounted,
  emptyCount,
  foundUnitKey,
  hasCounted,
  hasFoundUnit,
  indexSheet,
  isScanned,
  listDrafts,
  listedLotFor,
  loadDraft,
  matchCountScan,
  needsQuarantineZone,
  removeFoundStock,
  removeFoundUnit,
  restoreDraft,
  reviewCount,
  saveDraft,
  selectionTotals,
  setBulkCount,
  setFoundLookup,
  toDraft,
  uncountUnits,
  unitsLabel,
  upsertFoundStock,
  type CountScanMatch,
  type CountState,
  type FoundStock,
  type FoundUnit,
  type ReviewSelection,
  type ZoneCountDraft,
  type ZoneCountProduct,
  type ZoneCountSheet,
  type ZoneCountUnitLookup
} from './zoneCountSteps';
import './ZoneCount.scss';

type ZoneCountProps = {
  companyId: string;
  zoneId: string | null;
  setZoneId: (zoneId: string | null) => void;
  /** The counted zone's records, or null while loading / before a zone is chosen. */
  sheet: ZoneCountSheet | null;
  loadingSheet: boolean;
  sheetError: unknown;
  onReload: () => Promise<ZoneCountSheet | null>;
  lookupUnit: (unit: Record<string, unknown>) => Promise<ZoneCountUnitLookup>;
  lookupProduct: (code: string | null, gtin: string | null) => Promise<ZoneCountProduct | null>;
  onApply: (variables: ZoneCountApplyVariables) => Promise<ZoneCountApplyResult>;
  /** Where counts in progress are kept (tests pass their own). */
  storage?: Storage | null;
};

type Mode = 'add' | 'remove';
type ScanSource = 'camera' | 'hardware' | 'manual';
type Result =
  | { kind: 'applied'; data: ZoneCountApplyResult; zoneName: string }
  | { kind: 'finished'; matches: boolean; zoneName: string };

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

/**
 * The camera re-arms this fast (default 3 s) so a count runs at the pace of a hand moving packs past
 * it; the same read arriving again inside SAME_READ_WINDOW_MS — the pack still in frame — is
 * ignored without a sound. A handheld trigger is always deliberate and never ignored.
 */
const REARM_DELAY_MS = 250;
const SAME_READ_WINDOW_MS = 1500;
const DRAFT_SAVE_DELAY_MS = 400;

const CONTAINER_LABEL: Record<string, string> = { SSCC: 'Pallet', CA: 'Case', PK: 'Pack' };

const newId = () => `fs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const defaultStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const timeLabel = (at: number) =>
  new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const readKeyOf = (match: CountScanMatch): string => {
  switch (match.kind) {
    case 'unit':
      return `u:${match.ref.unit.serializedItemId}`;
    case 'container':
      return `c:${match.container.serializedItemId}`;
    case 'foundUnit':
      return `f:${foundUnitKey(match.serialNumber, match.middle12)}`;
    case 'bulk':
    case 'needsSerial':
      return `p:${match.product.key}`;
    case 'unknownProduct':
      return `x:${match.code}`;
    case 'unknownPallet':
      return `s:${match.code}`;
    default:
      return `t:${match.text}`;
  }
};

/**
 * Zone Count: scan a stock zone's label and count what is on the shelf against what the records say
 * the zone holds. The count screen lists the zone like the Verify screen lists an order — every
 * product with `counted / expected` — and each serialized unit is counted by scanning it (a sealed
 * case counts the units recorded inside), each bulk / OTC lot by entering its total. Serials the
 * zone has no record of are listed as found and looked up as they are scanned, so the operator sees
 * at once what the records know about them.
 *
 * Nothing is written while counting: the count is kept on this device (and survives a reload). The
 * review then sets out the differences — missing serials, found serials, lots short or over — with
 * everything the count can act on selected, and moves what the operator confirms to a quarantine
 * zone (or records a misplaced unit here) in one go.
 */
function ZoneCount({
  companyId,
  zoneId,
  setZoneId,
  sheet,
  loadingSheet,
  sheetError,
  onReload,
  lookupUnit,
  lookupProduct,
  onApply,
  storage = defaultStorage()
}: ZoneCountProps) {
  const navigate = useNavigate();
  const { discardScanResult, emitDecodeFeedback } = useScanner() || ({} as any);
  const lookupZoneCode = useZoneScanLookup({ companyId });

  // ─────────────────────────── the count ───────────────────────────
  const [count, setCountValue] = useState<CountState>(emptyCount);
  const countRef = useRef(count);
  // Written through synchronously: two trigger pulls can land before the re-render.
  const updateCount = useCallback((next: CountState) => {
    countRef.current = next;
    setCountValue(next);
  }, []);
  /** The zone whose count is in state; null until the resume question is settled. */
  const [countZoneId, setCountZoneId] = useState<string | null>(null);
  const countZoneIdRef = useRef<string | null>(null);
  // Keep the ref in lockstep with state — and write it eagerly in resetCount before any
  // found-unit lookup fires. Resume starts from countZoneId === null; if lookups captured
  // that null and the zone id landed on a later render, their results were discarded and
  // the UI spun on “Checking…” forever (count page and Review).
  countZoneIdRef.current = countZoneId;
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [resumeOffer, setResumeOffer] = useState<ZoneCountDraft | null>(null);

  const [mode, setModeValue] = useState<Mode>('add');
  const modeRef = useRef<Mode>('add');
  const [flash, setFlash] = useState<{ key: string | null; n: number }>({ key: null, n: 0 });
  const gateRef = useRef<{ key: string; at: number } | null>(null);
  const zoneCodeRef = useRef<string | null>(null);
  const productLookupRef = useRef(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [foundStockDraft, setFoundStockDraft] = useState<FoundStockDraft | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // ─────────────────────────── the review ───────────────────────────
  const [reviewing, setReviewing] = useState(false);
  const [selection, setSelection] = useState<ReviewSelection | null>(null);
  const [note, setNote] = useState('');
  const [quarantinePickerOpen, setQuarantinePickerOpen] = useState(false);
  const [destination, setDestination] = useState<ChosenZone | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  // ─────────────────────────── choosing the zone ───────────────────────────
  const [zonePickerOpen, setZonePickerOpen] = useState(false);
  const [zoneChoices, setZoneChoices] = useState<ScannedZone[] | null>(null);
  const [checkingZone, setCheckingZone] = useState(false);
  const checkingZoneRef = useRef(false);

  const index = useMemo(() => (sheet ? indexSheet(sheet) : null), [sheet]);
  const indexRef = useRef(index);
  indexRef.current = index;
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;

  const counting = !!sheet && countZoneId === sheet.zone.id;
  const summary = useMemo(() => (sheet ? countSummary(sheet, count) : null), [sheet, count]);
  const review = useMemo(
    () => (sheet && reviewing ? reviewCount(sheet, count) : null),
    [sheet, count, reviewing]
  );

  // Anything covering the list stops scans landing on it.
  const pausedRef = useRef(false);
  pausedRef.current =
    !counting ||
    reviewing ||
    !!resumeOffer ||
    !!foundStockDraft ||
    confirmLeave ||
    refreshing ||
    !!result;
  const scannerOn = counting && !reviewing && !resumeOffer && !result;

  // A scan published while no step was listening (or a label still in frame as the step changes)
  // must not be handed to the next step.
  useEffect(() => {
    discardScanResult?.();
    gateRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId, counting, reviewing]);

  const resetCount = useCallback(
    (zone: string | null) => {
      updateCount(emptyCount());
      countZoneIdRef.current = zone;
      setCountZoneId(zone);
      setStartedAt(Date.now());
      setReviewing(false);
      setSelection(null);
      setNote('');
      setDestination(null);
      setModeValue('add');
      modeRef.current = 'add';
    },
    [updateCount]
  );

  // ─────────────────────────── found-unit lookups ───────────────────────────

  const runLookup = useCallback(
    async (found: FoundUnit) => {
      const zoneAtCall = countZoneIdRef.current;
      try {
        const lookup = await lookupUnit({
          serialNumber: found.serialNumber,
          gtin: found.gtin,
          code: found.code,
          lot: found.lot,
          expiration: found.expiration
        });
        if (countZoneIdRef.current !== zoneAtCall) return;
        if (!hasFoundUnit(countRef.current, found.key)) return;
        updateCount(setFoundLookup(countRef.current, found.key, lookup || null, !lookup));
        if (lookup?.status === 'IN_THIS_ZONE') {
          alert(
            'Records changed',
            `Serial ${found.serialNumber} is now recorded in this zone. Refresh the records to count it.`,
            6,
            'warning'
          );
        }
      } catch {
        if (countZoneIdRef.current !== zoneAtCall) return;
        if (hasFoundUnit(countRef.current, found.key)) {
          updateCount(setFoundLookup(countRef.current, found.key, null, true));
        }
      }
    },
    [lookupUnit, updateCount]
  );

  const retryLookup = (key: string) => {
    const found = countRef.current.found.find((f) => f.key === key);
    if (!found) return;
    updateCount(setFoundLookup(countRef.current, key, null, false));
    runLookup(found);
  };

  const lookupPendingFound = (state: CountState) => {
    for (const found of state.found) if (!found.lookup) runLookup(found);
  };

  // ─────────────────────────── a sheet arrives ───────────────────────────

  useEffect(() => {
    if (!sheet || countZoneId === sheet.zone.id) return;
    const draft = storage ? loadDraft(storage, companyId, sheet.zone.id, Date.now()) : null;
    if (draft && draftCounted(draft) > 0) {
      setResumeOffer(draft);
      return;
    }
    setResumeOffer(null);
    resetCount(sheet.zone.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, countZoneId]);

  const resume = () => {
    if (!sheet || !resumeOffer) return;
    const restored = restoreDraft(sheet, resumeOffer);
    resetCount(sheet.zone.id);
    updateCount(restored);
    setStartedAt(resumeOffer.startedAt || Date.now());
    setResumeOffer(null);
    lookupPendingFound(restored);
  };

  const startOver = () => {
    if (!sheet) return;
    if (storage) clearDraft(storage, companyId, sheet.zone.id);
    setResumeOffer(null);
    resetCount(sheet.zone.id);
  };

  // The count is kept on this device as it goes, so a reload or a dropped app loses nothing.
  useEffect(() => {
    if (!counting || !sheet || !storage || result) return undefined;
    const timer = setTimeout(() => {
      if (hasCounted(count)) saveDraft(storage, companyId, toDraft(sheet, count, startedAt, Date.now()));
      else clearDraft(storage, companyId, sheet.zone.id);
    }, DRAFT_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [count, counting, sheet, storage, companyId, startedAt, result]);

  // ─────────────────────────── counting ───────────────────────────

  const counted = (flashKey: string | null, source: ScanSource) => {
    playSuccessTone();
    // Scandit's own decode beep too, for the camera it muted here (see SalesOrderVerify).
    if (source === 'camera') emitDecodeFeedback?.();
    if (flashKey) setFlash((f) => ({ key: flashKey, n: f.n + 1 }));
  };

  const scrollToProduct = (productKey: string) => {
    // Product keys are `fda:<id>` / `upc:<id>` — nothing in them needs escaping in a selector.
    const el = document.querySelector(`.zc-product[data-key="${productKey}"]`) as HTMLElement | null;
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  };

  const openFoundStockForCode = async (
    match: Extract<CountScanMatch, { kind: 'unknownProduct' }>,
    isScan: boolean
  ) => {
    if (productLookupRef.current) return;
    productLookupRef.current = true;
    try {
      const product = await lookupProduct(match.code || null, match.gtin || null);
      if (!product) {
        (isScan ? scanAlert : alert)(
          'Not in the catalog',
          `No product matches “${match.code}”. Set it aside and report it.`,
          6,
          'warning'
        );
        return;
      }
      if (product.kind === 'SERIALIZED' && isScan) {
        alert(
          'Not on record in this zone',
          `${product.productName} has no record here. If the pack has a 2D barcode, scan that to record the unit by its serial — or add it as found stock.`,
          6,
          'info'
        );
      }
      setFoundStockDraft({
        product,
        lot: match.lot,
        expiration: match.expiration,
        quantity: 1
      });
    } catch (e) {
      alert(
        'Could not look that up',
        operatorFacingMessage(e, 'Check the connection and scan it again.'),
        6,
        'error'
      );
    } finally {
      productLookupRef.current = false;
    }
  };

  const applyMatch = (match: CountScanMatch, source: ScanSource) => {
    const current = countRef.current;
    const removing = modeRef.current === 'remove';
    const zoneName = sheetRef.current?.zone.name || 'this zone';
    switch (match.kind) {
      case 'unit': {
        const id = match.ref.unit.serializedItemId;
        const serial = match.ref.unit.serialNumber;
        if (removing) {
          if (!isScanned(current, id)) {
            alert('Not counted', `Serial ${serial} is not on the count.`, 3, 'info');
            return;
          }
          updateCount(uncountUnits(current, [id]));
          counted(match.ref.product.key, source);
          return;
        }
        if (isScanned(current, id)) {
          playInfoTone();
          alert('Already counted', `Serial ${serial} is already counted.`, 3, 'info');
          return;
        }
        updateCount(countUnits(current, [id]));
        counted(match.ref.product.key, source);
        return;
      }
      case 'container': {
        const { container } = match;
        const label = CONTAINER_LABEL[container.type] || 'Container';
        const productKey = indexRef.current?.units.get(container.unitIds[0])?.product.key || null;
        if (removing) {
          const ids = container.unitIds.filter((id) => isScanned(current, id));
          if (!ids.length) {
            alert('Not counted', `Nothing in ${label.toLowerCase()} ${container.serialNumber} is on the count.`, 3, 'info');
            return;
          }
          updateCount(uncountUnits(current, ids));
          counted(productKey, source);
          alert(`${label} taken off`, `${unitsLabel(ids.length)} taken off the count.`, 3, 'success');
          return;
        }
        const fresh = container.unitIds.filter((id) => !isScanned(current, id));
        if (!fresh.length) {
          playInfoTone();
          alert('Already counted', `Every unit in ${label.toLowerCase()} ${container.serialNumber} is already counted.`, 3, 'info');
          return;
        }
        updateCount(countUnits(current, fresh));
        counted(productKey, source);
        alert(
          `${label} counted`,
          `${unitsLabel(fresh.length)} recorded inside ${label.toLowerCase()} ${container.serialNumber} counted.`,
          3,
          'success'
        );
        return;
      }
      case 'foundUnit': {
        const key = foundUnitKey(match.serialNumber, match.middle12);
        if (removing) {
          if (!hasFoundUnit(current, key)) {
            alert('Not counted', `Serial ${match.serialNumber} is not on the count.`, 3, 'info');
            return;
          }
          updateCount(removeFoundUnit(current, key));
          counted('found', source);
          return;
        }
        if (hasFoundUnit(current, key)) {
          playInfoTone();
          alert('Already noted', `Serial ${match.serialNumber} is already listed as found.`, 3, 'info');
          return;
        }
        const found: FoundUnit = {
          key,
          serialNumber: match.serialNumber,
          gtin: match.gtin,
          code: match.code,
          lot: match.lot,
          expiration: match.expiration,
          lookup: null
        };
        updateCount(addFoundUnit(current, found));
        // Heard, but not a match: a different tone from a counted unit.
        playInfoTone();
        if (source === 'camera') emitDecodeFeedback?.();
        setFlash((f) => ({ key: 'found', n: f.n + 1 }));
        alert(
          'Not on record in this zone',
          `Serial ${match.serialNumber} is listed under “Not on record”. Checking what the records know about it…`,
          3,
          'warning'
        );
        runLookup(found);
        return;
      }
      case 'unknownPallet':
        scanAlert(
          'Pallet not recorded here',
          'This zone has no record of that pallet. Open it and scan the units inside.',
          5,
          'warning'
        );
        return;
      case 'needsSerial':
        // A nudge, not a refusal: no fail tone (scanAlert would play one).
        alert(
          'Scan the 2D code',
          `${match.product.productName} is counted by serial number. Scan the small square 2D code on each pack.`,
          4,
          'warning'
        );
        return;
      case 'bulk': {
        const { product, lots } = match;
        if (lots.length > 1) {
          alert(
            'Several lots here',
            `${product.productName} has ${lots.length} lots in ${zoneName}. Enter each lot’s count on its card.`,
            5,
            'info'
          );
          setFlash((f) => ({ key: product.key, n: f.n + 1 }));
          scrollToProduct(product.key);
          return;
        }
        const lot = lots[0];
        if (removing) {
          if ((current.bulk[lot.key] ?? 0) <= 0) {
            alert('Not counted', `No units of ${product.productName} are counted yet.`, 3, 'info');
            return;
          }
          updateCount(bumpBulkCount(current, lot.key, -1));
        } else {
          updateCount(bumpBulkCount(current, lot.key, 1));
        }
        counted(product.key, source);
        return;
      }
      case 'unknownProduct':
        if (removing) {
          alert('Not counted', 'That product is not on the count.', 3, 'info');
          return;
        }
        openFoundStockForCode(match, source !== 'manual');
        return;
      default: {
        const text = match.text;
        if (!text) return;
        // Most often the zone label still in frame.
        if (text === zoneCodeRef.current || text === sheetRef.current?.zone.name) return;
        alert(
          'Scan a product',
          `“${text}” is not a product barcode. To count another zone, finish this count first.`,
          4,
          'info'
        );
      }
    }
  };

  const handleCountScan = (scan: any) => {
    const current = sheetRef.current;
    const idx = indexRef.current;
    if (!current || !idx || pausedRef.current) return;
    const source: ScanSource = scan?.isManualEntry
      ? 'manual'
      : scan?.source === 'hardware'
      ? 'hardware'
      : 'camera';
    const match = matchCountScan(current, idx, scan);
    if (source === 'camera') {
      const key = readKeyOf(match);
      const now = Date.now();
      const gate = gateRef.current;
      if (gate && gate.key === key && now - gate.at < SAME_READ_WINDOW_MS) {
        gate.at = now;
        return;
      }
      gateRef.current = { key, at: now };
    }
    applyMatch(match, source);
  };

  const switchMode = (next: Mode) => {
    if (next === modeRef.current) return;
    modeRef.current = next;
    setModeValue(next);
    // A deliberate switch: the next read is meant, even of the pack still in frame.
    gateRef.current = null;
  };

  const saveFoundStock = (stock: Omit<FoundStock, 'id'> & { id?: string }) => {
    const current = sheetRef.current;
    if (!current) return;
    const listed = !stock.id ? listedLotFor(current, stock.product.key, stock.lot, stock.expiration) : null;
    if (listed) {
      updateCount(bumpBulkCount(countRef.current, listed.key, stock.quantity));
      alert(
        'Added to the listed lot',
        `Lot ${listed.lot || '—'} is on record here, so ${unitsLabel(stock.quantity)} went on its count.`,
        5,
        'info'
      );
      scrollToProduct(stock.product.key);
    } else {
      updateCount(upsertFoundStock(countRef.current, { ...stock, id: stock.id || newId() }));
    }
    setFoundStockDraft(null);
  };

  const refreshRecords = async () => {
    const current = sheetRef.current;
    if (!current || refreshing) return;
    const draft = toDraft(current, countRef.current, startedAt, Date.now());
    const lookups = new Map<string, FoundUnit>(countRef.current.found.map((f) => [f.key, f]));
    setRefreshing(true);
    try {
      const fresh = await onReload();
      if (!fresh) {
        alert('Could not refresh', 'Check the connection and try again.', 5, 'error');
        return;
      }
      const restored = restoreDraft(fresh, draft);
      // Units still not on record are asked about again: what the records say may have changed.
      restored.found = restored.found.map((f) => ({ ...(lookups.get(f.key) || f), lookup: null, lookupFailed: false }));
      updateCount(restored);
      lookupPendingFound(restored);
      alert('Records refreshed', 'The count was kept and matched to the latest records.', 3, 'success');
    } finally {
      setRefreshing(false);
    }
  };

  // ─────────────────────────── review ───────────────────────────

  const openReview = () => {
    if (!sheet) return;
    setSelection(defaultSelection(reviewCount(sheet, countRef.current)));
    setReviewing(true);
  };

  // A found unit looked up after the review opened gets its default choice.
  useEffect(() => {
    if (!review || !selection) return;
    const missing = review.found.filter((f) => f.lookup && !(f.key in selection.found));
    if (!missing.length) return;
    setSelection((current) =>
      current
        ? {
            ...current,
            found: {
              ...current.found,
              ...Object.fromEntries(
                missing.map((f) => [f.key, f.lookup?.canQuarantine ? 'QUARANTINE' : 'SKIP'])
              )
            }
          }
        : current
    );
  }, [review, selection]);

  const input = review && selection ? applyInput(review, selection) : null;
  const totals = review && selection ? selectionTotals(review, selection) : null;
  const pendingLookups = review ? review.found.filter((f) => !f.lookup && !f.lookupFailed).length : 0;

  const startApply = () => {
    if (!input || !totals?.changes) return;
    if (needsQuarantineZone(input)) setQuarantinePickerOpen(true);
    else {
      setDestination(null);
      setConfirmApply(true);
    }
  };

  const doApply = async () => {
    if (!input || !sheet || applying) return;
    const zoneName = sheet.zone.name;
    setApplying(true);
    try {
      const data = await onApply({
        ...input,
        quarantineZoneId: needsQuarantineZone(input) ? destination?.id ?? null : null,
        note: note.trim() || null
      });
      if (storage) clearDraft(storage, companyId, sheet.zone.id);
      setConfirmApply(false);
      setResult({ kind: 'applied', data, zoneName });
      playSuccessTone();
    } catch (e) {
      setConfirmApply(false);
      alert(
        'Could not record the count',
        operatorFacingMessage(e, 'Nothing was recorded. Check the connection and try again.'),
        10,
        'error'
      );
      // The refusal is most often the records moving on: re-read them under the same count.
      await refreshRecords();
    } finally {
      setApplying(false);
    }
  };

  const finishWithoutChanges = () => {
    if (!sheet) return;
    if (storage) clearDraft(storage, companyId, sheet.zone.id);
    setConfirmFinish(false);
    setResult({ kind: 'finished', matches: !!review?.matches, zoneName: sheet.zone.name });
  };

  // ─────────────────────────── leaving / next zone ───────────────────────────

  const leave = () => {
    if (counting && hasCounted(countRef.current) && !result) setConfirmLeave(true);
    else navigate('/');
  };

  const countAnotherZone = () => {
    setResult(null);
    setResumeOffer(null);
    resetCount(null);
    setZoneId(null);
  };

  const recountZone = async () => {
    setResult(null);
    const zone = sheet?.zone.id || null;
    resetCount(zone);
    await onReload();
  };

  const discardCount = () => {
    if (sheet && storage) clearDraft(storage, companyId, sheet.zone.id);
    setConfirmLeave(false);
    resetCount(null);
    navigate('/');
  };

  // ─────────────────────────── choosing the zone ───────────────────────────

  const chooseZone = (zone: { id: string; name: string; zoneType?: string | null }, code?: string) => {
    if (zone.zoneType && zone.zoneType !== 'stock') {
      scanAlert(
        `${zone.name} is a quarantine zone`,
        'Zone Count counts stock zones. Scan the label on a stock zone.',
        6,
        'warning'
      );
      return;
    }
    zoneCodeRef.current = code || null;
    setZoneChoices(null);
    resetCount(null);
    setZoneId(zone.id);
  };

  const handleZoneScan = async (scan: any) => {
    if (checkingZoneRef.current) return;
    if (looksLikeProductScan(scan)) {
      scanAlert('That is a product barcode', 'Scan the zone label on the rack first.', 5, 'warning');
      return;
    }
    const code = (scan?.text || '').trim();
    if (!code) return;
    checkingZoneRef.current = true;
    setCheckingZone(true);
    const lookup = await lookupZoneCode(code);
    checkingZoneRef.current = false;
    setCheckingZone(false);
    if (lookup.error) {
      scanAlert(
        'Zone lookup failed',
        operatorFacingMessage(lookup.error, 'Could not look up that zone. Please try again.'),
        6,
        'error'
      );
      return;
    }
    if (lookup.zone) {
      if (lookup.zone.zoneType === 'stock') playSuccessTone();
      chooseZone(lookup.zone, code);
      return;
    }
    if (lookup.matches.length > 1) {
      setZoneChoices(lookup.matches);
      return;
    }
    scanAlert('Zone not found', `No zone matches “${code}”. Scan the zone label again, or choose it from the list.`, 6, 'error');
  };

  const drafts = !zoneId && storage ? listDrafts(storage, companyId, Date.now()) : [];

  // ─────────────────────────── render: choose a zone ───────────────────────────

  if (!zoneId) {
    return (
      <Page className="zone-count action-page">
        <div className="action-top-bar">
          <Button type="secondary" iconBefore={<FiChevronLeft />} {...click(() => navigate('/'))}>
            Back
          </Button>
          <div className="status">Zone Count</div>
        </div>
        <div className="action-page-body">
          <div className="zc-camera zc-camera--zone">
            <SharedScanner
              handleScan={handleZoneScan}
              handleManualEntry={() => setZonePickerOpen(true)}
              scanProfile={SCAN_PROFILE.ZONE}
            />
          </div>
          <div className="zc-intro">
            <h1>Scan a zone to count it</h1>
            <p>
              Scan the label on the rack. You will see everything the records say is in that zone,
              then count what is really on the shelf.
            </p>
            {checkingZone && <p className="zc-intro__checking">Looking up the zone…</p>}
          </div>
          <Button type="secondary" fullWidth {...click(() => setZonePickerOpen(true))}>
            Choose the zone from the list
          </Button>
          {drafts.length > 0 && (
            <section className="zc-drafts" aria-label="Counts in progress">
              <div className="zc-section-head">
                <h3>Counts in progress on this device</h3>
              </div>
              {drafts.map((draft) => (
                <button
                  key={draft.zoneId}
                  type="button"
                  className="zc-draft"
                  onClick={() => chooseZone({ id: draft.zoneId, name: draft.zoneName, zoneType: 'stock' })}
                >
                  <FiMapPin aria-hidden />
                  <span className="zc-draft__zone">{draft.zoneName}</span>
                  <span className="zc-draft__meta">
                    <FiClock aria-hidden /> {unitsLabel(draftCounted(draft))} · {timeLabel(draft.updatedAt)}
                  </span>
                </button>
              ))}
            </section>
          )}
        </div>

        <ZonePickerModal
          open={zonePickerOpen}
          onClose={() => setZonePickerOpen(false)}
          title="Which zone are you counting?"
          label="Stock zone"
          confirmLabel="Count this zone"
          companyId={companyId}
          zoneTypes={['stock']}
          // Opened by Manual Entry or "Choose the zone from the list", with this step's own camera
          // already live behind it — the operator asked for the list, so that is where it opens.
          initialView="list"
          scanTitle="Scan Zone"
          scanHint="Scan the label on the zone you are counting."
          onZoneChosen={(zone) => chooseZone({ id: zone.id, name: zone.name, zoneType: zone.zoneType })}
        />

        {zoneChoices && (
          <div className="zc-sheet-backdrop" role="dialog" aria-label="Choose the zone">
            <div className="zc-sheet">
              <p className="zc-sheet__title">More than one zone matches</p>
              <p className="zc-sheet__text">Tap the zone you are standing at.</p>
              <div className="zc-sheet__choices">
                {zoneChoices.map((zone) => (
                  <button
                    key={zone.id}
                    type="button"
                    className="zc-draft"
                    disabled={zone.zoneType !== 'stock'}
                    onClick={() => chooseZone(zone)}
                  >
                    <FiMapPin aria-hidden />
                    <span className="zc-draft__zone">{zone.name}</span>
                    <span className="zc-draft__meta">
                      {zone.zoneType === 'stock' ? zone.description || 'Stock zone' : 'Quarantine zone'}
                    </span>
                  </button>
                ))}
              </div>
              <Button type="secondary" fullWidth {...click(() => setZoneChoices(null))}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Page>
    );
  }

  // ─────────────────────────── render: loading the zone ───────────────────────────

  if (!sheet) {
    return (
      <Page className="zone-count action-page">
        <div className="action-top-bar">
          <Button type="secondary" iconBefore={<FiChevronLeft />} {...click(() => setZoneId(null))}>
            Zones
          </Button>
          <div className="status">Zone Count</div>
        </div>
        <div className="action-page-body">
          {loadingSheet || !sheetError ? (
            <div className="zc-loading">
              <FadeLoader color="var(--n0)" />
              <p>Loading the zone’s records…</p>
            </div>
          ) : (
            <div className="zc-empty">
              <FiAlertTriangle style={{ color: 'var(--r400)' }} />
              <p>
                {operatorFacingMessage(
                  sheetError,
                  'Could not load the zone’s records. Check the connection and try again.'
                )}
              </p>
              <Button type="primary" fullWidth {...click(() => onReload())}>
                Try again
              </Button>
              <Button type="secondary" fullWidth {...click(() => setZoneId(null))}>
                Choose another zone
              </Button>
            </div>
          )}
        </div>
      </Page>
    );
  }

  const zone = sheet.zone;
  const flashFor = (key: string) => (flash.key === key ? (flash.n % 2 ? 'a' : 'b') : null);

  // ─────────────────────────── render: review ───────────────────────────

  if (reviewing && review && selection && summary && totals && input) {
    const primaryLabel = review.matches
      ? 'Finish count'
      : totals.quarantine > 0
      ? `Quarantine ${unitsLabel(totals.quarantine)}${
          totals.moveHere ? ` · record ${totals.moveHere} here` : ''
        }`
      : totals.moveHere > 0
      ? `Record ${unitsLabel(totals.moveHere)} in ${zone.name}`
      : 'Nothing selected';
    return (
      <Page className="zone-count zone-count--review action-page">
        <div className="action-top-bar">
          <div className="order-details">
            <h1>Review · Zone {zone.name}</h1>
            <h2>
              {summary.counted} counted · {summary.expected} on record
            </h2>
          </div>
          <FiXCircle aria-label="Back to the count" onClick={() => setReviewing(false)} />
        </div>
        <div className="action-page-body">
          <ZoneCountReview
            review={review}
            summary={summary}
            selection={selection}
            onSelectionChange={(update) => setSelection((current) => (current ? update(current) : current))}
            onRetryLookup={retryLookup}
            note={note}
            onNoteChange={setNote}
          />
          <div className="zc-footer">
            {pendingLookups > 0 && (
              <p className="zc-footer__reason">
                Checking {pendingLookups} found serial{pendingLookups === 1 ? '' : 's'}…
              </p>
            )}
            <Button
              type="primary"
              fullWidth
              disabled={!review.matches && (!totals.changes || applying)}
              {...click(review.matches ? finishWithoutChanges : startApply)}
            >
              {primaryLabel}
            </Button>
            <Button type="secondary" fullWidth iconBefore={<FiArrowLeft />} {...click(() => setReviewing(false))}>
              Back to count
            </Button>
            {!review.matches && (
              <button type="button" className="zc-link zc-footer__link" onClick={() => setConfirmFinish(true)}>
                Finish without recording changes
              </button>
            )}
          </div>
        </div>

        <ZonePickerModal
          open={quarantinePickerOpen}
          onClose={() => setQuarantinePickerOpen(false)}
          title="Which quarantine zone is the stock going into?"
          label="Quarantine zone"
          confirmLabel="Use this zone"
          companyId={companyId}
          locationId={sheet.locationId}
          zoneTypes={['quarantine']}
          scanTitle="Scan Quarantine Zone"
          scanHint="Scan the label on the quarantine zone the stock is going into."
          onZoneChosen={(chosen) => {
            setDestination(chosen);
            setConfirmApply(true);
          }}
        />

        {confirmApply && (
          <div className="zc-sheet-backdrop" role="dialog" aria-label="Confirm the count">
            <div className="zc-sheet">
              <p className="zc-sheet__title">
                {destination ? `Quarantine into ${destination.name}?` : `Record in ${zone.name}?`}
              </p>
              <ul className="zc-sheet__list">
                {input.missingUnitIds.length + input.shortages.reduce((s, x) => s + x.quantity, 0) > 0 && (
                  <li>
                    <strong>
                      {unitsLabel(
                        input.missingUnitIds.length + input.shortages.reduce((s, x) => s + x.quantity, 0)
                      )}
                    </strong>{' '}
                    missing from {zone.name} leave its record for {destination?.name}.
                  </li>
                )}
                {input.foundUnits.filter((f) => f.action === 'QUARANTINE').length +
                  input.overages.reduce((s, x) => s + Number(x.quantity || 0), 0) >
                  0 && (
                  <li>
                    <strong>
                      {unitsLabel(
                        input.foundUnits.filter((f) => f.action === 'QUARANTINE').length +
                          input.overages.reduce((s, x) => s + Number(x.quantity || 0), 0)
                      )}
                    </strong>{' '}
                    found on the shelf are recorded in {destination?.name} — move them there now.
                  </li>
                )}
                {totals.moveHere > 0 && (
                  <li>
                    <strong>{unitsLabel(totals.moveHere)}</strong> recorded in another zone{' '}
                    {totals.moveHere === 1 ? 'is' : 'are'} recorded in {zone.name}.
                  </li>
                )}
              </ul>
              <div className="zc-sheet__actions">
                <Button type="primary" fullWidth disabled={applying} {...click(doApply)}>
                  {destination ? `Quarantine into ${destination.name}` : 'Record'}
                </Button>
                {destination && (
                  <Button
                    type="secondary"
                    fullWidth
                    disabled={applying}
                    {...click(() => {
                      setConfirmApply(false);
                      setQuarantinePickerOpen(true);
                    })}
                  >
                    Choose another zone
                  </Button>
                )}
                <Button type="secondary" fullWidth disabled={applying} {...click(() => setConfirmApply(false))}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {confirmFinish && (
          <div className="zc-sheet-backdrop" role="dialog" aria-label="Finish without recording">
            <div className="zc-sheet">
              <p className="zc-sheet__title">Finish without recording?</p>
              <p className="zc-sheet__text">
                The differences stay as they are in the records, and this count is cleared from the
                device.
              </p>
              <div className="zc-sheet__actions">
                <Button type="danger" fullWidth {...click(finishWithoutChanges)}>
                  Finish without recording
                </Button>
                <Button type="secondary" fullWidth {...click(() => setConfirmFinish(false))}>
                  Keep reviewing
                </Button>
              </div>
            </div>
          </div>
        )}

        <ResultPanel
          result={result}
          onAnother={countAnotherZone}
          onRecount={recountZone}
          onDone={() => navigate('/')}
        />

        {applying && (
          <div className="blocking-spinner-container" onClick={(e) => e.stopPropagation()}>
            <FadeLoader color="var(--n0)" />
          </div>
        )}
      </Page>
    );
  }

  // ─────────────────────────── render: count ───────────────────────────

  const toGo = summary?.toGo ?? 0;
  return (
    <Page className="zone-count action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>Zone {zone.name}</h1>
          <h2>Zone Count · {unitsLabel(sheet.expectedTotal)} on record</h2>
        </div>
        <FiXCircle aria-label="Close" onClick={leave} />
      </div>

      <div className="action-page-body">
        {scannerOn && (
          <div className="zc-camera">
            <SharedScanner
              handleScan={handleCountScan}
              handleManualEntry={() => setManualOpen(true)}
              scanProfile={SCAN_PROFILE.PRODUCT}
              rearmDelayMs={REARM_DELAY_MS}
              // The page sounds each read itself, once it knows what the read did.
              silentDecode
            />
          </div>
        )}

        {summary && (
          <div className="zc-summary">
            <div className="zc-summary__count">
              <span className="zc-summary__number">{summary.counted}</span>
              <span className="zc-summary__of">/ {summary.expected}</span>
            </div>
            <div className="zc-summary__label">
              <span className="zc-summary__heading">
                Units counted
                {toGo > 0 && <span className="zc-summary__togo">{toGo} to go</span>}
              </span>
              {summary.notOnRecord > 0 && (
                <small className="zc-summary__over">{summary.notOnRecord} not on record here</small>
              )}
            </div>
            <button
              type="button"
              className="zc-icon-button zc-summary__refresh"
              aria-label="Refresh the zone’s records"
              disabled={refreshing}
              onClick={refreshRecords}
            >
              <FiRefreshCw className={refreshing ? 'zc-spin' : undefined} />
            </button>
          </div>
        )}

        <div className="zc-mode" role="radiogroup" aria-label="Scan mode">
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'add'}
            className={['zc-mode__option', mode === 'add' && 'active'].filter(Boolean).join(' ')}
            onClick={() => switchMode('add')}
          >
            <FiPlusCircle aria-hidden /> Count
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'remove'}
            className={['zc-mode__option', 'zc-mode__option--remove', mode === 'remove' && 'active']
              .filter(Boolean)
              .join(' ')}
            onClick={() => switchMode('remove')}
          >
            <FiMinusCircle aria-hidden /> Take off
          </button>
        </div>
        {mode === 'remove' && (
          <p className="zc-remove-banner" role="status">
            <FiAlertTriangle aria-hidden /> Take-off mode — every unit you scan comes OFF the count.
          </p>
        )}

        {sheet.notes.length > 0 && (
          <details className="zc-notes">
            <summary>
              <FiInfo aria-hidden /> {sheet.notes.length} note{sheet.notes.length === 1 ? '' : 's'} about
              this zone’s records
            </summary>
            <ul>
              {sheet.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </details>
        )}

        {sheet.products.length === 0 && (
          <div className="zc-callout">
            <FiInfo aria-hidden />
            <div>
              <strong>No stock on record in {zone.name}</strong>
              <span>Scan anything you find on the shelf — it is listed below as not on record.</span>
            </div>
          </div>
        )}

        <div className="zc-products">
          {sheet.products.map((product) => (
            <ZoneCountProductCard
              key={product.key}
              product={product}
              count={count}
              flash={flashFor(product.key)}
              onSetBulk={(lotKey, value) => updateCount(setBulkCount(countRef.current, lotKey, value))}
            />
          ))}
        </div>

        <div className={['zc-found-wrap', flashFor('found') && `zc-found-wrap--flash-${flashFor('found')}`].filter(Boolean).join(' ')}>
          <ZoneCountFoundList
            found={count.found}
            foundStock={count.foundStock}
            onRemoveUnit={(key) => updateCount(removeFoundUnit(countRef.current, key))}
            onRetryLookup={retryLookup}
            onEditStock={(stock) => setFoundStockDraft({ ...stock })}
            onRemoveStock={(id) => updateCount(removeFoundStock(countRef.current, id))}
            onAddStock={() => setFoundStockDraft({ product: null, quantity: 1 })}
          />
        </div>

        <div className="zc-footer">
          <Button type="primary" fullWidth {...click(openReview)}>
            Review count
          </Button>
          {toGo > 0 && hasCounted(count) && (
            <p className="zc-footer__reason">
              {unitsLabel(toGo)} on record {toGo === 1 ? 'is' : 'are'} not counted yet — the review
              lists {toGo === 1 ? 'it' : 'them'} as missing.
            </p>
          )}
        </div>
      </div>

      {manualOpen && (
        <ZoneCountManualEntry
          mode={mode}
          zoneName={zone.name}
          onSubmit={(entry) => handleCountScan(entry)}
          onClose={() => setManualOpen(false)}
        />
      )}

      {foundStockDraft && (
        <ZoneCountFoundStockSheet
          initial={foundStockDraft}
          zoneName={zone.name}
          lookupProduct={(code) => lookupProduct(code, null)}
          onSave={saveFoundStock}
          onRemove={(id) => {
            updateCount(removeFoundStock(countRef.current, id));
            setFoundStockDraft(null);
          }}
          onClose={() => setFoundStockDraft(null)}
        />
      )}

      {resumeOffer && (
        <div className="zc-sheet-backdrop" role="dialog" aria-label="Resume the count">
          <div className="zc-sheet">
            <p className="zc-sheet__title">Resume your count of {zone.name}?</p>
            <p className="zc-sheet__text">
              {unitsLabel(draftCounted(resumeOffer))} counted on this device, last at{' '}
              {timeLabel(resumeOffer.updatedAt)}. It is matched to the latest records.
            </p>
            <div className="zc-sheet__actions">
              <Button type="primary" fullWidth {...click(resume)}>
                Resume count
              </Button>
              <Button type="secondary" fullWidth {...click(startOver)}>
                Start over
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmLeave && (
        <div className="zc-sheet-backdrop" role="dialog" aria-label="Leave the count">
          <div className="zc-sheet">
            <p className="zc-sheet__title">Leave the count?</p>
            <p className="zc-sheet__text">
              Nothing has been recorded. Your count stays on this device — open Zone Count and scan{' '}
              {zone.name} again to pick it up.
            </p>
            <div className="zc-sheet__actions">
              <Button type="primary" fullWidth {...click(() => setConfirmLeave(false))}>
                Keep counting
              </Button>
              <Button type="secondary" fullWidth {...click(() => navigate('/'))}>
                Leave
              </Button>
              <Button type="danger" fullWidth {...click(discardCount)}>
                Discard this count
              </Button>
            </div>
          </div>
        </div>
      )}

      <ResultPanel
        result={result}
        onAnother={countAnotherZone}
        onRecount={recountZone}
        onDone={() => navigate('/')}
      />
    </Page>
  );
}

function ResultPanel({
  result,
  onAnother,
  onRecount,
  onDone
}: {
  result: Result | null;
  onAnother: () => void;
  onRecount: () => void;
  onDone: () => void;
}) {
  const lines: string[] = [];
  if (result?.kind === 'applied') {
    const { data } = result;
    const into = data.quarantineZone?.name;
    if (data.missingQuantity) {
      lines.push(`${unitsLabel(data.missingQuantity)} missing from ${result.zoneName} moved to ${into} by record.`);
    }
    if (data.foundQuantity) {
      lines.push(`${unitsLabel(data.foundQuantity)} found on the shelf recorded in ${into}.`);
    }
    if (data.movedHereUnits) {
      lines.push(`${unitsLabel(data.movedHereUnits)} recorded in ${result.zoneName}.`);
    }
    if (data.packagingCount) {
      lines.push(
        `${data.packagingCount} case${data.packagingCount === 1 ? '' : 's'} / pack${
          data.packagingCount === 1 ? '' : 's'
        } followed ${data.packagingCount === 1 ? 'its' : 'their'} contents.`
      );
    }
  }
  return (
    <div className={['result-panel', 'zc-result', !!result && 'open'].filter(Boolean).join(' ')}>
      {result && (
        <>
          <div className="result-message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>
              {result.kind === 'applied'
                ? 'Count recorded'
                : result.matches
                ? `${result.zoneName} matches the records`
                : 'Count finished'}
            </h1>
            {result.kind === 'applied' ? (
              lines.map((line) => <p key={line}>{line}</p>)
            ) : (
              <p>
                {result.matches
                  ? 'Every unit on record was counted, and nothing extra was found.'
                  : 'Nothing was recorded. The differences stay as they are.'}
              </p>
            )}
            {result.kind === 'applied' && result.data.foundQuantity > 0 && (
              <p className="zc-result__todo">
                Move the found stock into {result.data.quarantineZone?.name} now.
              </p>
            )}
          </div>
          <div className="result-actions">
            <Button type="primary" fullWidth {...click(onAnother)}>
              Count another zone
            </Button>
            <Button type="secondary" fullWidth {...click(onRecount)}>
              Count {result.zoneName} again
            </Button>
            <Button type="secondary" fullWidth {...click(onDone)}>
              Done
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default ZoneCount;
